import { useMemo } from 'react';
import { useMultimodalLive } from './useMultimodalLive';

type AdapterProps = {
  // Match useGeminiLive signature superset, all optional here
  selectedDocument?: string | null;
  activeSessionId?: string | null;
  topicId?: string | null;
  setMessages?: (updater: (prev: any[]) => any[]) => void;
  setUnsavedMessageCount?: (updater: (prev: number) => number) => void;
  messageSeqRef?: React.MutableRefObject<number>;
  getCanvasEl?: (() => HTMLElement | null) | null;
  screenStream?: MediaStream | null;
  getYouTubePlayer?: (() => any) | null;
  currentYouTubeVideoId?: string | null;
  currentSlideContext?: {
    slide: {
      id?: string;
      title?: string | null;
      slide_type?: string | null;
      section?: string | null;
      tutor_prompt?: string | null;
      duration_minutes?: number | null;
      slide_order?: number;
      completed?: boolean;
    } | null;
    lessonTitle?: string;
    progress?: {
      total?: number;
      completed?: number;
      pendingTitles?: string[];
    };
  };
  startCurrentSession?: () => Promise<void>;
  endCurrentSession?: (finalHistory?: any[]) => Promise<void>;
  appendMessage?: (message: any) => void;
  onAgentActivityChange?: (isActive: boolean) => void;
};

// This adapter returns the same surface as useGeminiLive so ChatPage can switch hooks behind a flag with zero UI changes.
export function useLiveAdapter(props: AdapterProps) {
  const live = useMultimodalLive({
    selectedDocument: props.selectedDocument,
    activeSessionId: props.activeSessionId,
    topicId: props.topicId,
    setMessages: props.setMessages,
    setUnsavedMessageCount: props.setUnsavedMessageCount,
    appendMessage: props.appendMessage,
    messageSeqRef: props.messageSeqRef,
    onAgentActivityChange: props.onAgentActivityChange,
    getCanvasEl: props.getCanvasEl,
    screenStream: props.screenStream,
    getYouTubePlayer: props.getYouTubePlayer,
    currentYouTubeVideoId: props.currentYouTubeVideoId,
    slideContext: props.currentSlideContext,
  });

  // Stubs for push-to-talk and VAD until Phase 4/5
  const noop = () => {};

  const wrappedConnect = async (mode?: string) => {
    try {
      if (props.startCurrentSession) {
        await props.startCurrentSession();
      }
    } catch (e) {
      // ignore; fallback to current active session id
    }
    await live.handleConnectAgent(mode);
  };

  const wrappedStop = async () => {
    try {
      if (props.endCurrentSession) {
        await props.endCurrentSession();
      }
    } catch (e) {
      // ignore persistence errors; proceed to disconnect anyway
    }
    live.handleStopConversation();
  };

  return useMemo(() => ({
    // State
    agentStatus: live.agentStatus,
    isAgentActive: live.isAgentActive,
    isUserMuted: live.isUserMuted,
    sessionDuration: live.sessionDuration,
    isPushToTalkLocked: live.isPushToTalkLocked,
    isAgentTyping: live.isAgentTyping,
    isUserSpeakingVad: live.isUserSpeakingVad,
    // Handlers
    handleConnectAgent: wrappedConnect,
    handleStopConversation: wrappedStop,
    toggleMute: live.toggleMute,
    handlePushToTalkStart: live.handlePushToTalkStart,
    handlePushToTalkEnd: live.handlePushToTalkEnd,
    handleToggleLock: live.handleToggleLock,
    // Expose raw live ref if needed later
    _live: live,
  }), [live, wrappedConnect, wrappedStop]);
}
